import { useState } from "react"
import { useSearchParams } from "react-router"
import { Button, Empty, Flex, Input, Result, Spin, Typography } from "antd"
import {
  AnalyticsContext,
  PointBalanceGuard,
  ResourceUploadScope,
} from "~/components"
import { getStaffVideoTaskPath } from "~/common/router"
import { useHotVideoStore } from "~/store/hotVideo"
import { InputPanel } from "~/pages/VideoRecreate/InputPanel"
import { ResultPanel } from "~/pages/VideoRecreate/ResultPanel"
import { useFusionImport } from "./useFusionImport"
import styles from "./index.css"

const FusionWorkspace = ({ fusionId }: { fusionId: string }) => {
  const { state, retry } = useFusionImport(fusionId)
  const draftRevision = useHotVideoStore((value) => value.draftRevision)
  const negativeWords = useHotVideoStore(
    (value) => value.staffPreview?.optimize_prompt.product_info.negative_words,
  )
  const setResourcesPending = useHotVideoStore(
    (value) => value.setResourcesPending,
  )

  if (state.status === "loading")
    return (
      <Flex
        className={styles.placeholder}
        align="center"
        justify="center"
        vertical
        gap={16}
      >
        <Spin />
        <Typography.Text>{state.message}</Typography.Text>
      </Flex>
    )
  if (state.status === "error")
    return (
      <Result
        status="error"
        title="无法导入融合记录"
        subTitle={state.message}
        extra={<Button onClick={retry}>重试导入</Button>}
      />
    )
  if (state.status === "empty")
    return (
      <Empty
        className={styles.placeholder}
        description={
          <>
            请前往
            <Typography.Link
              href="http://10.23.2.76:3000/hot-videos"
              target="_blank"
              rel="noopener noreferrer"
            >
              热门素材库
            </Typography.Link>
            挑选热门素材，完成提示词融合后跳转至此页，或输入融合记录 ID 导入
          </>
        }
      />
    )

  return (
    <AnalyticsContext category="Staff 融合脚本生成视频">
      {negativeWords && (
        <Typography.Paragraph ellipsis={{ rows: 2, expandable: true }}>
          来源反向词 / 禁用与限制：{negativeWords}
        </Typography.Paragraph>
      )}
      <ResourceUploadScope
        key={draftRevision}
        onPendingChange={setResourcesPending}
      >
        <Flex className={styles.workspace}>
          <InputPanel
            key={draftRevision}
            staff
          />
          <ResultPanel taskListPath={getStaffVideoTaskPath} />
        </Flex>
      </ResourceUploadScope>
    </AnalyticsContext>
  )
}

export const StaffRecreate = () => {
  const [params, setParams] = useSearchParams()
  const fusionId = params.get("fusionId")?.trim() || ""
  const [value, setValue] = useState(fusionId)

  return (
    <Flex
      vertical
      gap={16}
    >
      <PointBalanceGuard />
      {!fusionId && (
        <Flex
          vertical
          gap={12}
        >
          <Typography.Title level={4}>融合脚本生成视频</Typography.Title>
          <Input.Search
            className={styles.importInput}
            aria-label="融合记录 ID"
            placeholder="输入融合记录 ID"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            enterButton="导入融合结果"
            onSearch={(id) => {
              if (id.trim()) setParams({ fusionId: id.trim() })
            }}
          />
        </Flex>
      )}
      <FusionWorkspace
        key={fusionId}
        fusionId={fusionId}
      />
    </Flex>
  )
}
